interface BankNumberFieldProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  imgSrc?: string;
  imgAlt: string;
  label: string;
  bankDigits: string[];
  digitRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  handleDigitChange: (value: string, index: number) => void;
}

const BankNumberField = ({
  imgSrc,
  imgAlt,
  label,
  bankDigits,
  digitRefs,
  handleDigitChange,
  required,
  className,
}: BankNumberFieldProps) => {
  return (
    <>
      <label className="block text-sm font-medium mb-1">
        <span className="inline-flex items-center gap-1">
          {imgSrc && <img src={imgSrc} alt={imgAlt} className="mr-2 h-4" />}
          {label}
        </span>
      </label>
      <div className="flex justify-center gap-5 mb-4">
        {bankDigits.map((digit, idx) => (
          <input
            type="tel" // keeps numeric keypad
            inputMode="numeric" // hints browser/OS to use number pad
            pattern="[0-9]*" // ensures only digits
            maxLength={1}
            value={digit}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              handleDigitChange(value, idx);

              if (value && idx < bankDigits.length - 1) {
                digitRefs.current[idx + 1]?.focus();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
              if (!pasted) return;

              const newDigits = [...bankDigits];
              for (
                let i = 0;
                i < pasted.length && idx + i < bankDigits.length;
                i++
              ) {
                newDigits[idx + i] = pasted[i];
                handleDigitChange(pasted[i], idx + i);
              }

              // Focus last filled box
              const nextIndex = Math.min(
                idx + pasted.length,
                bankDigits.length - 1
              );
              digitRefs.current[nextIndex]?.focus();
            }}
            className={`${className} w-10 h-10 p-2 rounded-md text-black text-center text-lg font-semibold border border-gray-300 focus:outline-none`}
            required={required}
            ref={(el) => {
              digitRefs.current[idx] = el;
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace") {
                if (bankDigits[idx] === "" && idx > 0) {
                  e.preventDefault();
                  digitRefs.current[idx - 1]?.focus();
                  handleDigitChange("", idx - 1);
                } else {
                  handleDigitChange("", idx);
                }
              }
            }}
          />
        ))}
      </div>
    </>
  );
};

export default BankNumberField;
